import type { ComparisonSlug } from "@listou/shared/constants";

import { amazon } from "../content/comparatifs/amazon";
import { kadolog } from "../content/comparatifs/kadolog";
import { mesenvies } from "../content/comparatifs/mesenvies";
import { milirose } from "../content/comparatifs/milirose";

/**
 * The comparison pages, one content file per competitor in `content/comparatifs/`.
 *
 * Comparative advertising is legal in France only if every claim about the competitor is
 * objective and verifiable (Code de la consommation, L122-1). So each claim says what their
 * own site says, `sources` lists where, and `checkedOn` is the day it was read: a claim
 * nobody can trace back to a page is one to leave out. Re-check before changing a row.
 *
 * The long text is Markdown rather than MDX: the server renders these pages from source
 * with Bun, and MDX would need a compiler in Vite, in Bun's runtime, in the tests and in
 * `bun build`. The structured parts (table, FAQ, sources) stay typed, since the FAQ also
 * feeds the structured data.
 */

/**
 * `partial` is for a feature that exists with a limit the text spells out. `unstated` is
 * for one the competitor's documentation does not describe: that is all we can verify,
 * and a "no" would claim more than that.
 */
export type Verdict = "yes" | "no" | "partial" | "unstated";
export type Cell = { verdict: Verdict; text: string };
export type ComparisonRow = { feature: string; listou: Cell; them: Cell };

export type Comparison = {
  name: string;
  site: string;
  /** The footer link, worded as people search it: "Alternative à Mes Envies". */
  linkLabel: string;
  /** ISO day the sources were read. */
  checkedOn: string;
  seoTitle: string;
  seoDescription: string;
  h1: string;
  /** The answer, in two or three sentences: what a search snippet or an assistant quotes. */
  summary: string;
  rows: ComparisonRow[];
  /** Markdown, from `##` down: the detailed comparison, criterion by criterion. */
  body: string;
  /** Said plainly: a page that finds no merit in the other side convinces nobody. */
  chooseThem: string[];
  chooseListou: string[];
  faq: [question: string, answer: string][];
  sources: { label: string; url: string }[];
};

export const COMPARISONS: Record<ComparisonSlug, Comparison> = {
  mesenvies,
  milirose,
  kadolog,
  amazon,
};

/** schema.org FAQPage: the questions as the page shows them, word for word. */
export const comparisonJsonLd = (comparison: Comparison) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: comparison.faq.map(([question, answer]) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
});
